import { TooltipProvider } from '@/components/tooltip'
import { useThemeSync } from '@/hooks/useThemeSync'
import { SidebarLayout } from '@frontend/components/layouts/sidebar'
import { TopNavLayout } from '@frontend/components/layouts/topnav'
import { AppConfig, getStore } from '@frontend/core'
import {
  AuthGuard,
  ErrorBoundary,
  MQTTProvider,
  OrganisationSwitcher,
  RegionQueryPersistence,
  RegionSwitcher,
  SidebarOrganisationSwitcher,
  SidebarRegionSwitcher,
  SportableBatteryNotifications,
  SportableData,
  SportableHardware,
  SportableLivePositionData,
  SportableLiveSessionData,
  SportableMetricsServer,
  SportableMetricsServerOfflineMode,
  SportablePitch,
  useAppSelector
} from '@frontend/react'
import { SportableBreadcrumbs } from '@frontend/react/app/breadcrumbs'
import { useRegionSwitchHandler } from '@frontend/react/app/hooks'
import { Modal } from '@frontend/react/modules/modal'
import { ModalNotification } from '@frontend/react/modules/notifications'
import { SentryUser } from '@frontend/react/modules/sentry/components'
import { Circle, Loader } from 'lucide-react'
import { JSX, StrictMode, useMemo } from 'react'
import { Provider } from 'react-redux'
import {
  BrowserRouter,
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
  useRoutes,
  useSearchParams
} from 'react-router-dom'
// Theme-aware Toaster: wires sonner's `theme` to the app theme atom so toasts
// follow light/dark instead of always rendering white (raw `sonner` Toaster).
import { Toaster } from '@frontend/ui'
import { NavigationGuardProvider } from './NavigationGuardProvider'
import { useNavigationGuard } from './navigation-guard-context'
import { SidebarNavUser } from './sidebar-nav-user'
import { TopNavUser } from './topnav-user'

export function SportableRoutes({ appConfig }: { appConfig: AppConfig }) {
  const { guardedNavigate } = useNavigationGuard()
  const location = useLocation()
  const version = useAppSelector((state) => state.app.version)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const element = useRoutes(appConfig.routes)

  const NavExtra = appConfig.topnavExtra
  const NavUser = appConfig.topnavUser ?? TopNavUser
  const NavLogo = appConfig.topnavLogo

  const handleTopnavNavigate = (url: string) => {
    const org = searchParams.get('org')
    const team = searchParams.get('team')
    const session = searchParams.get('session')
    const params = new URLSearchParams()
    if (org) params.set('org', org)
    if (team) params.set('team', team)
    if (session) params.set('session', session)
    const qs = params.toString()
    navigate(qs ? `${url}?${qs}` : url)
  }

  const landingRoute = appConfig.routes.find((route) => route.isLanding)

  const selectedRoute = appConfig.routes.find((route) => {
    if (!route.path) return false

    return (
      matchPath({ path: route.path, end: true }, location.pathname) !== null
    )
  })

  const navMainItems = useMemo(() => {
    return appConfig.routes
      .filter((route) => route.mainNav)
      .map((route) => {
        return {
          title: route.name || '',
          url: route.path || '',
          icon: route.icon || Circle,
          isActive: selectedRoute && selectedRoute.path === route.path,
          items: []
        }
      })
  }, [appConfig.routes, selectedRoute])

  // If logged in render the side bar layout //
  if (location.pathname === '/login') {
    return <>{element}</>
  }

  if (!selectedRoute) {
    if (landingRoute?.path) {
      return <Navigate to={landingRoute.path} />
    }
    return null
  }
  const exempt = selectedRoute?.exemptFromAuthentication
    ? selectedRoute.exemptFromAuthentication
    : appConfig?.exemptFromAuthentication
  const authElement = exempt ? element : <AuthGuard>{element}</AuthGuard>

  return (
    <>
      {!selectedRoute.viewLayout && <>{authElement}</>}
      {selectedRoute.viewLayout === 'sidebar' && (
        <SidebarLayout
          navMainItems={navMainItems}
          navuser={<SidebarNavUser />}
          navorganisation={<SidebarOrganisationSwitcher />}
          breadcrumbs={<SportableBreadcrumbs selectedRoute={selectedRoute} />}
          regions={appConfig.sidebarRegions ? undefined : <RegionSwitcher />}
          sidebarFooterExtra={
            appConfig.sidebarRegions ? <SidebarRegionSwitcher /> : undefined
          }
          hideHeader={selectedRoute.hideHeader}
          onNavigate={guardedNavigate}
          version={version}
        >
          {authElement}
        </SidebarLayout>
      )}
      {selectedRoute.viewLayout === 'topnav' && (
        <TopNavLayout
          navItems={navMainItems}
          navuser={<NavUser />}
          navorganisation={
            appConfig.hideTopnavOrganisation ? null : (
              <OrganisationSwitcher hideIfSingle />
            )
          }
          navextra={NavExtra ? <NavExtra /> : undefined}
          logo={NavLogo ? <NavLogo /> : undefined}
          onNavigate={handleTopnavNavigate}
          appName={appConfig.name}
        >
          {authElement}
        </TopNavLayout>
      )}
    </>
  )
}

export function SportableAppContent() {
  const appConfig = useAppSelector((state) => state.app.config)
  const { connections } = useAppSelector((state) => state.servers)

  useRegionSwitchHandler()

  if (
    appConfig.pitchSide &&
    !connections.metricsServer.hasMadeSuccessfulConnection
  ) {
    return (
      <div className='h-[95vh]'>
        <div className='flex h-full items-center justify-center'>
          <Loader className='animate-spin' />
          <p id='loading-message' className='ml-2'>
            Connecting to server...
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <SentryUser />

      {/* Pitch side */}
      {appConfig.pitchSide && <SportableData />}
      {appConfig.pitchSide && <SportablePitch />}
      {appConfig.pitchSide && <SportableHardware />}
      {appConfig.pitchSide && <SportableMetricsServerOfflineMode />}

      {/* Notifications */}
      {appConfig.pitchSide && <SportableBatteryNotifications />}

      {/* Live data streams */}
      {appConfig.livePositionData && <SportableLivePositionData />}
      {appConfig.liveSessionData && <SportableLiveSessionData />}

      <SportableRoutes appConfig={appConfig} />
    </>
  )
}

export function SportableApp({
  appConfig,
  CustomHeader,
  store
}: {
  appConfig: AppConfig
  CustomHeader?: (props: any) => JSX.Element
  store: ReturnType<typeof getStore>
}) {
  useThemeSync()

  const children = (
    <Provider store={store}>
      <MQTTProvider>
        <Modal />
        <Toaster />
        <ModalNotification />
        <TooltipProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <NavigationGuardProvider>
                {!appConfig.pitchSide && <RegionQueryPersistence />}

                {CustomHeader && <CustomHeader appConfig={appConfig} />}

                {appConfig.pitchSide && <SportableMetricsServer />}

                <SportableAppContent />
              </NavigationGuardProvider>
            </BrowserRouter>
          </ErrorBoundary>
        </TooltipProvider>
      </MQTTProvider>
    </Provider>
  )
  if (appConfig.reactStrictMode) {
    return <StrictMode>{children}</StrictMode>
  }
  return children
}
